/**
 * 資料提供模組
 *	轉換 server API 資料到 client 端。
 */

//-----------------------------------------------------------------------
// 載入模組
import { DataProvider, UpdateManyResult } from 'ra-core';
import * as networkLib from "../lib/network";

//-----------------------------------------------------------------------
// defines
import { API_RESULT_CODE } from '../common/defines';
import { messages } from '../common/strings';
import { MEMBER_API, SYSTEM_API, STATISTICS_API } from '../common/protocol';
import { GetDataParams, GetDataRes, GetListParams, GetListRes } from '../common/protocol';
import { CreateDataParams, CreateDataRes, ModifyDataParams, ModifyDataRes } from '../common/protocol';
import { DeleteDataParams, DeleteDataRes } from '../common/protocol';
import { sleep } from '../lib/misc';

interface DataInfo
{
	path	: string
	prefix	: string
	fields	?:
	{
		in		: string
		out		: string
		encode	?: boolean
	}[]
}

const dataMap = new Map<string, DataInfo>();

dataMap.set('member',				{ path: MEMBER_API.Path,					prefix: 'm_' });
dataMap.set('memberLog',			{ path: MEMBER_API.Path + '/log',			prefix: 'ml_',	fields: [ { in: 'memberId', out: 'm_id' } ] });
dataMap.set('memberPermission',		{ path: MEMBER_API.Path + '/pm',			prefix: '' });
dataMap.set('source',				{ path: SYSTEM_API.Path + '/src',			prefix: 's_' });
dataMap.set('region',				{ path: SYSTEM_API.Path + '/rgn',			prefix: 'r_',	fields: [ { in: 'sourceId', out: 's_id' } ] });
dataMap.set('regionAll',			{ path: SYSTEM_API.Path + '/rgn/all',		prefix: 'r_',	fields: [ { in: 'sourceId', out: 's_id' } ] });
dataMap.set('keyword',				{ path: SYSTEM_API.Path + '/kw',			prefix: 'k_' });
dataMap.set('keywordAll',			{ path: SYSTEM_API.Path + '/kw/all',		prefix: 'k_' });
dataMap.set('kol',					{ path: SYSTEM_API.Path + '/kol',			prefix: 'o_' });
dataMap.set('kolAll',				{ path: SYSTEM_API.Path + '/kol/all',		prefix: 'o_' });
dataMap.set('manuallyChannel',		{ path: SYSTEM_API.Path + '/mch',			prefix: 'mc_',	fields: [ { in: 'sourceId', out: 's_id' }, { in: 'keywordId', out: 'k_id' } ] });
dataMap.set('schedule',				{ path: SYSTEM_API.Path + '/scd',			prefix: 'st_',	fields: [ { in: 'memberId', out: 'm_id' } ] });
dataMap.set('channel',				{ path: STATISTICS_API.Path + '/ch',		prefix: 'c_',	fields: [ { in: 'name', out: 'name', encode: true }, { in: 'sourceId', out: 's_id' }, { in: 'regionId', out: 'r_id' }, { in: 'kolId', out: 'o_id' } ] });
dataMap.set('target',				{ path: STATISTICS_API.Path + '/tg',		prefix: 't_',	fields: [ { in: 'sourceId', out: 's_id' }, { in: 'regionId', out: 'r_id' }, { in: 'channelId', out: 'c_id' }, { in: 'mediaType', out: 'type' } ] });
dataMap.set('searchLog',			{ path: STATISTICS_API.Path + '/sl',		prefix: 'sl_',	fields: [ { in: 'sourceId', out: 's_id' }, { in: 'keywordId', out: 'k_id' }, { in: 'channelId', out: 'c_id' }, { in: 'targetId', out: 't_id' } ] });
dataMap.set('scheduleTargetLog',	{ path: STATISTICS_API.Path + '/stl',		prefix: 'c_',	fields: [ { in: 'sourceId', out: 's_id' }, { in: 'regionId', out: 'r_id' }, { in: 'kolId', out: 'o_id' }, { in: 'keywordId', out: 'k_id' } ] });

//-----------------------------------------------------------------------
function convertFieldName(dataInfo: DataInfo, fieldName: string) : string
{
	if(dataInfo.fields)
	{
		for(let field of dataInfo.fields)
		{
			if(field.in === fieldName)
				return dataInfo.prefix + field.out;
		}
	}

	return dataInfo.prefix + fieldName;
}

//-----------------------------------------------------------------------
// eslint-disable-next-line import/no-anonymous-default-export
export default () : DataProvider =>
({
	getList: async (resource, params) =>
	{
		try
		{
			let	dataInfo = dataMap.get(resource);
			if(typeof dataInfo === 'undefined')
				return Promise.reject({ message: `${resource} not set!`, status: 404 });

			let	getListParam : GetListParams =
			{
				page		: params.pagination?.page ?? 1,
				perPage		: params.pagination?.perPage ?? 10,
				sortKey		: convertFieldName(dataInfo, params.sort?.field ?? 'id'),
				sortOrder	: params.sort?.order as 'ASC' | 'DESC',
				filter		: {}
			};
			for(let key in params.filter)
			{
				if(key === 'id')
					getListParam.filter[dataInfo.prefix + key] = params.filter[key];
				else if(key === 'timeRange' || key === 'year' || key === 'quarter' || key === 'month')
					getListParam.filter[key] = params.filter[key];
				else
				{
					let	fieldName = key;
					let	fieldValue = params.filter[key];

					if(dataInfo.fields)
					{
						for(let field of dataInfo.fields)
						{
							if(field.in === fieldName)
							{
								if(typeof fieldValue === 'string')
									fieldValue = fieldValue.trim();

								if(field.encode)
									fieldValue = encodeURI(fieldValue);

								fieldName = field.out;
								break;
							}
						}
					}

					getListParam.filter[dataInfo.prefix + fieldName] = fieldValue;
				}
			}

			let	res = await networkLib.post(dataInfo.path, '/list', getListParam) as GetListRes<any>;

			if(res.resultCode !== API_RESULT_CODE.Succeeded)
				return Promise.reject(messages[res.resultCode]);

			return { data: res.list, total: res.total };
		}
		catch(e)
		{
			console.log("錯誤: " + e);
			throw e;
		}
	},

	getOne: async (resource, params) =>
	{
		try
		{
			let	dataInfo = dataMap.get(resource);
			if(typeof dataInfo === 'undefined')
				return Promise.reject({ message: `${resource} not set!`, status: 404 });

			let	getDataParam : GetDataParams = { id: params.id };
			let	res = await networkLib.post(dataInfo.path, '/get', getDataParam) as GetDataRes<any>;

			if(res.resultCode !== API_RESULT_CODE.Succeeded)
				return Promise.reject(messages[res.resultCode]);

			return { data: res.data };
		}
		catch(e)
		{
			console.log("錯誤: " + e);
			throw e;
		}
	},

	getMany: async (resource, params) =>
	{
		let	dataInfo = dataMap.get(resource);
		if(typeof dataInfo === 'undefined')
			return Promise.reject({ message: `${resource} not set!`, status: 404 });

		let	data : any[] = [];
		let	count = 0;

		function dec() { count--; }

		for(let id of params.ids)
		{
			let	getDataParam : GetDataParams = { id: id };

			count++;
			networkLib.post(dataInfo.path, '/get', getDataParam).then((res: GetDataRes<any>) =>
			{
				if(res.resultCode !== API_RESULT_CODE.Succeeded)
					console.log("getManay() error: " + JSON.stringify(res));
				else
					data.push(res.data);

				dec();
			}).catch((e) =>
			{
				console.log("getManay() error: " + e);
				dec();
			});
		}

		while(count > 0)
			await sleep(10);

		return { data: data };
	},

	getManyReference: async (resource, params) =>
	{
		try
		{
			let	dataInfo = dataMap.get(resource);
			if(typeof dataInfo === 'undefined')
				return Promise.reject({ message: `${resource} not set!`, status: 404 });

			let	getListParam : GetListParams =
			{
				page		: params.pagination.page,
				perPage		: params.pagination.perPage,
				sortKey		: convertFieldName(dataInfo, params.sort.field),
				sortOrder	: params.sort.order as 'ASC' | 'DESC',
				filter		: {}
			};
			for(let key in params.filter)
			{
				if(key === 'id')
					getListParam.filter[dataInfo.prefix + key] = params.filter[key];
				else if(key === 'timeRange' || key === 'year' || key === 'quarter' || key === 'month')
					getListParam.filter[key] = params.filter[key];
				else
				{
					let	fieldName = key;
					let	fieldValue = params.filter[key];

					if(dataInfo.fields)
					{
						for(let field of dataInfo.fields)
						{
							if(field.in === fieldName)
							{
								if(typeof fieldValue === 'string')
									fieldValue = fieldValue.trim();

								if(field.encode)
									fieldValue = encodeURI(fieldValue);

								fieldName = field.out;
								break;
							}
						}
					}

					getListParam.filter[dataInfo.prefix + fieldName] = fieldValue;
				}
			}
			getListParam.filter[params.target] = params.id;

			let	res = await networkLib.post(dataInfo.path, '/list', getListParam) as GetListRes<any>;

			if(res.resultCode !== API_RESULT_CODE.Succeeded)
				return Promise.reject(messages[res.resultCode]);

			return { data: res.list, total: res.total };
		}
		catch(e)
		{
			console.log("錯誤: " + e);
			throw e;
		}
	},

	update: async (resource, params) =>
	{
		try
		{
			let	dataInfo = dataMap.get(resource);
			if(typeof dataInfo === 'undefined')
				return Promise.reject({ message: `${resource} not set!`, status: 404 });

			let	modifyDataParam : ModifyDataParams = { id: params.id, ...params.data };
			let	res = await networkLib.post(dataInfo.path, '/modify', modifyDataParam) as ModifyDataRes;

			if(res.resultCode !== API_RESULT_CODE.Succeeded)
				return Promise.reject(messages[res.resultCode]);

			return { data: res as any };
		}
		catch(e)
		{
			console.log("錯誤: " + e);
			throw e;
		}
	},

	updateMany: async (resource, params) =>
	{
		let	dataInfo = dataMap.get(resource);
		if(typeof dataInfo === 'undefined')
			return Promise.reject({ message: `${resource} not set!`, status: 404 });

		let	updated : UpdateManyResult<any> = { data: [] };
		let	count = 0;

		function dec() { count--; }

		for(let id of params.ids)
		{
			let	modifyDataParam : ModifyDataParams = { id: id, ...params.data };

			count++;
			networkLib.post(dataInfo.path, '/modify', modifyDataParam).then((res: ModifyDataRes) =>
			{
				if(res.resultCode !== API_RESULT_CODE.Succeeded)
					console.log("updateMany() error: " + JSON.stringify(res));
				else if(updated.data)
					updated.data.push({ id: id, ...res.data });

				dec();
			}).catch((e) =>
			{
				console.log("updateMany() error: " + e);
				dec();
			});
		}

		while(count > 0)
			await sleep(10);

		return updated;
	},

	create: async (resource, params) =>
	{
		try
		{
			let	dataInfo = dataMap.get(resource);
			if(typeof dataInfo === 'undefined')
				return Promise.reject({ message: `${resource} not set!`, status: 404 });

			console.log("Create: " + JSON.stringify(params.data));

			let	createDataParam : CreateDataParams = { ...params.data };
			let	res = await networkLib.post(dataInfo.path, '/create', createDataParam) as CreateDataRes;

			if(res.resultCode !== API_RESULT_CODE.Succeeded)
				return Promise.reject(messages[res.resultCode]);

			return { data: res as any };
		}
		catch(e)
		{
			console.log("錯誤: " + e);
			throw e;
		}
	},

	delete: async (resource, params) =>
	{
		try
		{
			let	dataInfo = dataMap.get(resource);
			if(typeof dataInfo === 'undefined')
				return Promise.reject({ message: `${resource} not set!`, status: 404 });

			let	deleteDataParam : DeleteDataParams = { id: params.id };
			let	res = await networkLib.post(dataInfo.path, '/del', deleteDataParam) as DeleteDataRes;

			if(res.resultCode !== API_RESULT_CODE.Succeeded)
				return Promise.reject(messages[res.resultCode]);

			return { data: res as any };
		}
		catch(e)
		{
			console.log("錯誤: " + e);
			throw e;
		}
	},

	deleteMany: async (resource, params) =>
	{
		return { data: [] };
	}
});
